// @flow

import _ from 'lodash';
import bemify from 'bemify-js';
import React, { PureComponent } from 'react';

import { Image as OptimizedImage } from '@zapier/common-components';

import { getInitialsFromString } from 'app/common/CommonUtils';
import 'app/common/components/Avatar.scss';

const getNameInitials = (name: string): string => {
  const initials = getInitialsFromString(name);
  const first = _.first(initials);

  if (!first) {
    return '';
  }

  if (initials.length === 1) {
    return first;
  }

  return `${first}${_.last(initials)}`;
};

type AvatarProps = {
  name: string,
  className?: string,
  size: number,
  url: string,
};

const bem = bemify('avatar');

const Avatar = (componentProps: AvatarProps) => {
  const { name, url, size, ...props } = componentProps;
  const initials = name ? getNameInitials(name) : null;
  const fontSize = Math.floor(size * 0.45);
  const sizing = { width: size, height: size };

  return (
    <div {...props} className={bem(props.className)} style={sizing}>
      {url && (
        <div className={bem('__image')}>
          <OptimizedImage alt={name} height={size} src={url} width={size} />
        </div>
      )}
      {!url && initials ? (
        <div className={bem('__initials')} style={{ fontSize }}>
          {initials}
        </div>
      ) : null}
    </div>
  );
};

class AvatarStateful extends PureComponent<AvatarProps, { url: string }> {
  static defaultProps = {
    size: 36,
  };

  state = {
    url: '',
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;

    if (this.props.url) {
      this.tryLoadAvatar(this.props.url);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: AvatarProps) {
    if (nextProps.url && nextProps.url !== this.props.url) {
      this.tryLoadAvatar(nextProps.url);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  tryLoadAvatar(url: string) {
    const image = new Image();
    image.onload = () => {
      this.setUrl(url, url);
    };
    image.onerror = () => {
      this.setUrl(url, '');
    };
    image.src = url || this.props.url;
  }

  // The reason to use 2 params is to protect against race conditions
  // where the prop url might update before an image url has loaded or errored.
  // The condition is just a gatekeeper to ensure that the url (or empty '')
  // corresponds to the current prop value.
  setUrl(whichUrl: string, value: string) {
    if (this._isMounted && this.props.url === whichUrl) {
      this.setState({ url: value });
    }
  }

  render() {
    const { url } = this.state;
    return <Avatar {...this.props} url={url} />;
  }
}

export default AvatarStateful;
